import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Switch,
} from '@mui/material';
import {
  CheckCircle,
  Payment,
  Event,
  Warning,
  EmojiEvents,
  Security,
  AdminPanelSettings,
  Gavel,
  Add,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useMandalName, useDistribution } from '../hooks/useMandalName';
import { useFeatures } from '../hooks/useFeatures';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import { ACCENTS, tint } from '../theme/accents';
import { formatTime, weekdayName } from '../utils/datetime';
import { formatINR } from '../utils/currency';


const Rules = () => {
  const mandalName = useMandalName();
  const { sentence: distributionSentence } = useDistribution();
  const { features, loaded: featuresLoaded } = useFeatures();
  const { isAdmin } = useAuth();
  const [rules, setRules] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Payment',
    isEnforced: true,
  });

  useEffect(() => {
    fetchRules();
    fetchSettings();
  }, []);

  const fetchRules = async () => {
    try {
      const response = await axios.get('/api/rules');
      setRules(response.data);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    } finally {
      setLoading(false);
    }
  };

  // The key points below quote real amounts, so read them from settings rather
  // than hardcoding figures that go stale the moment an admin changes one.
  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/rules/settings');
      setSettings(response.data);
    } catch (error) {
      // Falls back to generic wording if settings are unavailable
    }
  };

  const handleCreateRule = async () => {
    try {
      await axios.post('/api/rules', formData);
      toast.success('Rule created successfully');
      setCreateDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        category: 'Payment',
        isEnforced: true,
      });
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create rule');
    }
  };

  // Categories borrow the same accents the rest of the app uses for these
  // domains, so a Payment rule is gold here and on the Payments page.
  const CATEGORY_ACCENT = {
    Payment: ACCENTS.emerald,
    Meeting: ACCENTS.teal,
    Penalty: ACCENTS.vermilion,
    Distribution: ACCENTS.magenta,
    Security: ACCENTS.indigo,
    Admin: ACCENTS.gold,
    General: ACCENTS.silver,
  };

  const getCategoryIcon = (category) => {
    const sx = { color: CATEGORY_ACCENT[category] || ACCENTS.silver };
    switch (category) {
      case 'Payment':
        return <Payment sx={sx} />;
      case 'Meeting':
        return <Event sx={sx} />;
      case 'Penalty':
        return <Warning sx={sx} />;
      case 'Distribution':
        return <EmojiEvents sx={sx} />;
      case 'Security':
        return <Security sx={sx} />;
      case 'Admin':
        return <AdminPanelSettings sx={sx} />;
      default:
        return <Gavel sx={sx} />;
    }
  };

  // A group that has switched Distribution off does not run a draw, so its rule is
  // left out too (the rule itself stays on file)
  const distributionOn = features.featureSilverDistribution;
  const visibleRules = featuresLoaded
    ? rules.filter((rule) => distributionOn || rule.category !== 'Distribution')
    : [];
  const hiddenRuleCount = featuresLoaded ? rules.length - visibleRules.length : 0;

  return (
    <Box>
      <PageHeader
        eyebrow="The agreement"
        accent={ACCENTS.silver}
        title="Rules & Regulations"
        subtitle="What the group has agreed, and the figures being charged today."
        actions={isAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
            Add Rule
          </Button>
        )}
      />

      <Paper sx={{ p: 3, mb: 3, background: (t) => `linear-gradient(140deg, ${tint(ACCENTS.gold, 0.16)} 0%, ${t.palette.background.paper} 70%)`, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" gutterBottom >
          {mandalName}
        </Typography>
        <Typography variant="body1" >
          These rules govern the operation of our investment group. All members are expected to comply with these regulations to ensure transparency and fairness.
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {visibleRules.length === 0 && featuresLoaded && !loading && (
          <Grid item xs={12}>
            <EmptyState
              icon={<Gavel />}
              accent={ACCENTS.silver}
              title="No rules recorded"
              description="The group's written agreement lives here. Add the first rule to start it off."
            />
          </Grid>
        )}
        {isAdmin && hiddenRuleCount > 0 && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              {hiddenRuleCount} distribution {hiddenRuleCount === 1 ? 'rule is' : 'rules are'} not shown because the distribution section is switched off (Admin Panel &gt; Features).
            </Typography>
          </Grid>
        )}
        {visibleRules.map((rule) => (
          <Grid item xs={12} md={6} key={rule.id}>
            <Card
              sx={{
                height: '100%',
                opacity: rule.is_active ? 1 : 0.62,
                borderStyle: rule.is_active ? 'solid' : 'dashed',
                '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.3s' },
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box sx={{ mr: 2 }}>
                    {getCategoryIcon(rule.category)}
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1 }}>
                    <Typography variant="overline" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                      Rule {rule.rule_number}
                    </Typography>
                    <Typography variant="h6" component="div" sx={{ lineHeight: 1.3 }}>
                      {rule.title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    {!rule.is_active && (
                      <Chip label="Not enforced" size="small" variant="outlined" />
                    )}
                    <Chip
                      label={rule.category}
                      size="small"
                      sx={{
                        bgcolor: (t) => tint(CATEGORY_ACCENT[rule.category] || ACCENTS.silver, 0.16),
                        color: CATEGORY_ACCENT[rule.category] || ACCENTS.silver,
                        border: '1px solid',
                        borderColor: (t) => tint(CATEGORY_ACCENT[rule.category] || ACCENTS.silver, 0.4),
                      }}
                    />
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {rule.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mt: 4, p: 3 }}>
        <Typography variant="h6">
          Where the numbers stand today
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The rules above are the group's written agreement. These are the live
          figures the app is charging right now, taken from system settings.
        </Typography>
        <List>
          {/* Derived from system_settings, so these can never drift from the
              amounts the app actually charges. */}
          <ListItem>
            <ListItemIcon>
              <CheckCircle sx={{ color: ACCENTS.emerald }} />
            </ListItemIcon>
            <ListItemText
              primary="Monthly contribution"
              secondary={
                settings
                  ? `Base share is ${formatINR(settings.monthlyAmount)}. Each member pays that multiplied by their own share count — a member on 2 shares pays ${formatINR(settings.monthlyAmount * 2)}.`
                  : 'Each member pays the base share multiplied by their share count.'
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle sx={{ color: ACCENTS.emerald }} />
            </ListItemIcon>
            <ListItemText
              primary="Payment deadline"
              secondary={
                settings
                  ? `Due by the ${settings.latePenaltyDays}th of the month. After that a ${formatINR(settings.penaltyAmount)} late penalty applies.`
                  : 'Contributions are due early in the month; a late penalty applies afterwards.'
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle sx={{ color: ACCENTS.emerald }} />
            </ListItemIcon>
            <ListItemText
              primary="Meeting attendance"
              secondary={
                settings
                  ? `Meetings are held on the first ${weekdayName(settings.meetingDay)} of every month at ${formatTime(settings.meetingTime)}. Attendance is expected.`
                  : 'Meetings are monthly and attendance is expected.'
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle sx={{ color: ACCENTS.emerald }} />
            </ListItemIcon>
            <ListItemText 
              primary="Transparency"
              secondary="All financial records are open for viewing by all members"
            />
          </ListItem>
          {distributionOn && (
            <ListItem>
              <ListItemIcon>
                <CheckCircle sx={{ color: ACCENTS.emerald }} />
              </ListItemIcon>
              <ListItemText
                primary={distributionSentence}
                secondary={
                  settings
                    ? (Number(settings.silverDistributionMonths) > 0
                      ? `A member is selected at random every ${Number(settings.silverDistributionMonths) === 1 ? 'month' : `${settings.silverDistributionMonths} months`}.`
                      : 'A member is selected at random. The group keeps no fixed schedule.')
                    : 'A member is selected at random on the distribution schedule.'
                }
              />
            </ListItem>
          )}
          <ListItem>
            <ListItemIcon>
              <CheckCircle sx={{ color: ACCENTS.emerald }} />
            </ListItemIcon>
            <ListItemText
              primary="Spending the fund"
              secondary="Fund money is spent only on agreed purchases such as gold or silver. Every expense is recorded with its amount, quantity and rate, and is visible to all members."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle sx={{ color: ACCENTS.emerald }} />
            </ListItemIcon>
            <ListItemText 
              primary="Emergency Provisions"
              secondary="Members can request others to pay on their behalf in case of family emergencies"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Create Rule Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Rule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rule Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                helperText="Detailed explanation of the rule"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <MenuItem value="Payment">Payment</MenuItem>
                <MenuItem value="Meeting">Meeting</MenuItem>
                <MenuItem value="Penalty">Penalty</MenuItem>
                <MenuItem value="Distribution">Distribution</MenuItem>
                <MenuItem value="Security">Security</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="General">General</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* Was a two-item select whose handler compared a boolean against
                  the string 'true', so it could only ever store false. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  height: '100%',
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: (t) => tint(formData.isEnforced ? ACCENTS.emerald : ACCENTS.silver, 0.08),
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formData.isEnforced ? 'Enforced' : 'Not enforced'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.isEnforced ? 'Applies to everyone' : 'Recorded but not applied'}
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(formData.isEnforced)}
                  onChange={(e) => setFormData({ ...formData, isEnforced: e.target.checked })}
                  inputProps={{ 'aria-label': 'Rule is enforced' }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateRule} 
            variant="contained"
            disabled={!formData.title || !formData.description}
          >
            Create Rule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Rules;